import { useEffect, useState } from "react";
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialRequestOptionsJSON,
  type PublicKeyCredentialCreationOptionsJSON,
} from "@simplewebauthn/browser";
import { Fingerprint, ArrowRight } from "lucide-react";
import { Field, Notice, Spinner } from "./Common";
import { errorMessage } from "../api";
export async function authPost<T>(
  path: string,
  body: unknown = {},
): Promise<T> {
  const response = await fetch(`/auth/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "same-origin",
  });
  const data = (await response.json()) as T & { error?: string };
  if (!response.ok)
    throw new Error(data.error || "Sign-in could not be completed.");
  return data;
}
export function Login({ onSignedIn }: { onSignedIn: () => Promise<unknown> }) {
  const [setup, setSetup] = useState(false),
    [code, setCode] = useState(""),
    [label, setLabel] = useState("My iPhone"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    const setupCode = new URLSearchParams(location.hash.slice(1)).get("setup");
    if (setupCode && /^[a-f0-9]{64}$/.test(setupCode)) {
      setCode(setupCode);
      setSetup(true);
      history.replaceState(null, "", location.pathname + location.search);
    }
  }, []);
  const signIn = async () => {
    setBusy(true);
    setError("");
    try {
      if (setup) {
        const options = await authPost<PublicKeyCredentialCreationOptionsJSON>(
          "register/options",
          { code: code.trim(), label },
        );
        const response = await startRegistration({ optionsJSON: options });
        await authPost("register/verify", response);
      } else {
        const options =
          await authPost<PublicKeyCredentialRequestOptionsJSON>(
            "login/options",
          );
        const response = await startAuthentication({ optionsJSON: options });
        await authPost("login/verify", response);
      }
      const returnTo = new URLSearchParams(location.search).get("returnTo");
      if (returnTo?.startsWith("/oauth/authorize?")) location.assign(returnTo);
      else await onSignedIn();
    } catch (e) {
      setError(
        e instanceof Error && e.name === "NotAllowedError"
          ? "Sign-in was cancelled. Tap the button to try again."
          : errorMessage(e),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="login-page">
      <div className="login-panel">
        <img
          src="/brand-mark.svg"
          width="64"
          height="64"
          alt="AI Link Logistics"
        />
        <p className="muted">AI Link Invoices</p>
        <h1>
          {setup ? "Set up this device" : "Your invoices, wherever you are."}
        </h1>
        <p className="muted">
          {setup
            ? "Use a one-time code from the account owner to create your passkey."
            : "Sign in with Face ID, Touch ID or your device passcode."}
        </p>
        {error && <Notice type="error">{error}</Notice>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void signIn();
          }}
        >
          {setup && (
            <>
              <Field label="Setup code" required>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  required
                />
              </Field>
              <Field label="Device name" required>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  maxLength={80}
                  required
                />
              </Field>
            </>
          )}
          <button className="primary" disabled={busy}>
            {busy ? <Spinner /> : <Fingerprint size={22} />}{" "}
            {setup ? "Create passkey" : "Sign in with passkey"}
          </button>
        </form>
        <button
          className="text-button"
          disabled={busy}
          onClick={() => {
            setSetup(!setup);
            setError("");
          }}
        >
          {setup ? "Back to sign in" : "I have a setup code"}
          <ArrowRight size={16} />
        </button>
        <p className="muted small">A private workspace for your business.</p>
      </div>
    </main>
  );
}
